import { fg } from '@atlaskit/platform-feature-flags/fg';

import { createAtlAttributionHeader } from '../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../common/fetch/fetch-agg-response';
import { logger } from '../common/utils/logger';
import { QueryIntent } from '../constants';

import { getFrontendQueryIntent } from './getFrontendQueryIntent';
import {
	getIntentDetectionGQLQuery,
	INTENT_DETECTION_OPERATION_NAME,
} from './getIntentDetectionGQLQuery';
import type { DetectionMetadata, IntentClassificationData, IntentDetectionResponse } from './types';

// Jira-family product keys that support NL→JQL (jira, jira-product-discovery, jira-servicedesk).
// Used to guard against over-triggering JIRA_NATURAL_LANGUAGE_QUERY on non-Jira surfaces.
const JIRA_FAMILY_APP_IDS = new Set(['jira', 'jira-product-discovery', 'jira-servicedesk']);

export type QueryIntentVariables = {
	siteId: string;
	query: string;
	locale: string;
	orgId: string;
	metadata?: {
		numberOfRecentDocuments?: number;
	};
};

export type QueryIntentArgs = QueryIntentVariables & {
	workspaceId: string;
	cloudId: string;
	product: string;
	selectedProduct?: string;
	setQueryIntent: (intent: QueryIntent) => void;
	setTransformedQuery: (transformedQuery: string) => void;
	setIntentData?: (intentData: IntentClassificationData) => void;
	onError?: (error: Error) => void;
	setRequestId: (requestId: string) => void;
	setTraceId?: (traceId: string) => void;
	setIntentDetectionMetadata?: (detectionMetadata: DetectionMetadata) => void;
};

export const getQueryIntent = async ({
	query,
	siteId,
	locale,
	metadata,
	cloudId,
	orgId,
	workspaceId,
	product,
	selectedProduct,
	setQueryIntent,
	setTransformedQuery,
	setIntentData,
	setRequestId,
	setTraceId,
	setIntentDetectionMetadata,
	onError,
}: QueryIntentArgs): Promise<void> => {
	const handleError = (error: Error) => {
		setQueryIntent(QueryIntent.NONE);
		setTransformedQuery('');
		onError?.(error);
		logger.error({ error, functionName: 'getQueryIntent: handleError' });
	};

	try {
		const frontendQueryIntent = getFrontendQueryIntent(query);
		// Intent from the frontend has a higher priority than the BE
		// Add any experiment and gate exclusion logic in `getFrontendQueryIntent` to prevent messy conditionals in this function
		if (!!frontendQueryIntent) {
			setQueryIntent(frontendQueryIntent.queryIntent);
			setTransformedQuery(frontendQueryIntent.transformedQuery);
			return;
		}

		const aggResponse = await fetchAggResponse<QueryIntentVariables, IntentDetectionResponse>({
			graphQuery: getIntentDetectionGQLQuery(),
			variables: {
				query,
				siteId,
				locale,
				orgId,
				...(metadata ? { metadata } : {}),
			},
			operationName: INTENT_DETECTION_OPERATION_NAME,
			// we're using the cache to make sure the number of calls remain the same
			// make sure the cache key format is different from the actual intent detection call
			cache: {
				ttl: 5 * 60 * 1000,
				key: `shadow-intent-detection-${query}`,
			},
			headers: createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId,
				product,
			}),
		});

		// Trace / request IDs from the gateway HTTP response (for analytics and debugging).
		const gatewayTracingHeaders = aggResponse.gatewayTracingHeaders ?? {};
		const response = aggResponse?.data?.knowledgeDiscovery.intentDetection;
		if (
			!response ||
			!aggResponse?.data ||
			(aggResponse?.errors && aggResponse?.errors.length > 0) ||
			(response?.errors && response?.errors.length > 0)
		) {
			// errors show up at the top level or in the response
			let errorMessage = aggResponse?.errors?.[0]?.message;
			if (response?.errors && response?.errors.length > 0) {
				errorMessage = response?.errors?.[0]?.message;
			}
			const error = errorMessage ? new Error(errorMessage) : new Error('getQueryIntent error');
			handleError(error);
			return;
		}

		// Intent from the BE
		let queryIntent = response.intent?.classification as QueryIntent;
		if (queryIntent === QueryIntent.VISUAL && !fg('hybrid_orchestrator_enable_image_search')) {
			queryIntent = QueryIntent.NATURAL_LANGUAGE_QUERY;
		}
		if (
			(queryIntent === QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY ||
				queryIntent === QueryIntent.JIRA_NLQ_KEYWORD) &&
			selectedProduct &&
			!JIRA_FAMILY_APP_IDS.has(selectedProduct) &&
			fg('kd_fix_sain_jira_nql_query_intent_overtriggering')
		) {
			queryIntent = QueryIntent.NATURAL_LANGUAGE_QUERY;
		}

		setQueryIntent(queryIntent);
		setTransformedQuery(response.intent?.transformedQuery);
		setIntentData?.(response.intent as IntentClassificationData);
		if (response.detectionMetadata) {
			setIntentDetectionMetadata?.(response.detectionMetadata);
		}
		setRequestId(gatewayTracingHeaders['atl-request-id'] ?? '');
		setTraceId?.(gatewayTracingHeaders['x-trace-id'] ?? '');
	} catch (error) {
		handleError(error as Error);
	}
};
